"use client";

import * as React from "react";
import { getRumClient } from "./rum";

export interface ObservabilityErrorBoundaryProps {
  children: React.ReactNode;
  /**
   * Fallback rendered when a descendant throws. Either a static node, or a
   * render function called with the error and a `reset` callback that
   * clears the boundary's error state (useful for "Try again" buttons).
   *
   * If omitted, the boundary renders `null` on error — the broken subtree
   * unmounts but the error doesn't propagate further. Apps should provide
   * a meaningful fallback for production UX.
   */
  fallback?:
    | React.ReactNode
    | ((error: Error, reset: () => void) => React.ReactNode);
  /**
   * Called after the error has been recorded to RUM. Use this for
   * additional reporting (Sentry, custom telemetry) or to trigger
   * application-level recovery.
   */
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
}

interface State {
  error: Error | null;
}

/**
 * React error boundary that routes render-time errors to CloudWatch RUM
 * via {@link getRumClient}. Drop into your component tree at any level
 * where you want to contain failure — typically the root layout for
 * full-page protection, or per-feature for isolated regions.
 *
 * RUM not initialized (SSR, before `<ObservabilityProvider>` mounts):
 * caught errors render the fallback but are not reported. The `onError`
 * callback still fires.
 *
 * @example
 * ```tsx
 * import { ObservabilityErrorBoundary } from "@juiceco-io/observability-sdk/react";
 *
 * <ObservabilityErrorBoundary
 *   fallback={(error, reset) => (
 *     <div>
 *       <p>Something went wrong.</p>
 *       <button onClick={reset}>Try again</button>
 *     </div>
 *   )}
 * >
 *   <App />
 * </ObservabilityErrorBoundary>
 * ```
 */
export class ObservabilityErrorBoundary extends React.Component<
  ObservabilityErrorBoundaryProps,
  State
> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const rum = getRumClient();
    rum?.recordError(error);
    this.props.onError?.(error, errorInfo);
  }

  reset = (): void => {
    this.setState({ error: null });
  };

  render(): React.ReactNode {
    const { error } = this.state;
    if (error) {
      const { fallback } = this.props;
      if (typeof fallback === "function") return fallback(error, this.reset);
      if (fallback !== undefined) return fallback;
      return null;
    }
    return this.props.children;
  }
}
